import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { GUIDED_EDIT_DEFAULT_TIMEZONE } from "@common/interfaces/adsManagerGuidedEdit";
import { scheduleInputToEventWall } from "@common/utils/adsetSchedule";

dayjs.extend(utc);
dayjs.extend(timezone);

/** IANA zone for interpreting event-wall strings; falls back to guided-edit default. */
export function resolveEventWallTimezone(
  eventTimezone: string | undefined
): string {
  return eventTimezone?.trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;
}

/**
 * Normalize any schedule wire value (ISO with offset/Z or naive wall) to
 * `YYYY-MM-DDTHH:mm` in the event IANA timezone. Same as
 * {@link scheduleInputToEventWall} (shared with GET guided-edit).
 */
export function scheduleInputToEventWallMinute(
  value: string | undefined,
  eventTimezone: string
): string {
  return scheduleInputToEventWall(
    value,
    resolveEventWallTimezone(eventTimezone)
  );
}

/** Backward-compatible alias for existing call sites. */
export const wireScheduleToEventWall = scheduleInputToEventWallMinute;

/**
 * Event wall → `datetime-local` `value`.
 * Uses the same calendar digits as the event zone (not the browser’s local offset),
 * so “14:00” stays 14:00 in the event timezone on the control and matches review/API.
 */
export function eventWallToDatetimeLocalValue(
  wallInput: string,
  eventTimezone: string
): string {
  if (!wallInput?.trim()) return "";
  const wall = scheduleInputToEventWallMinute(wallInput, eventTimezone);
  if (!wall) return "";
  return wall.length > 16 ? wall.slice(0, 16) : wall;
}

/**
 * `datetime-local` change → event wall `YYYY-MM-DDTHH:mm` in `eventTimezone`.
 * Interprets the control’s date/time digits as wall time in the event zone (not browser local).
 */
export function datetimeLocalValueToEventWall(
  localValue: string,
  eventTimezone: string
): string {
  if (!localValue?.trim()) return "";
  const tz = resolveEventWallTimezone(eventTimezone);
  const normalized =
    localValue.length > 16 ? localValue.slice(0, 16) : localValue;
  const d = dayjs.tz(normalized, tz);
  return d.isValid() ? d.format("YYYY-MM-DDTHH:mm") : "";
}
